import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCrumb } from "@poseidon/ui/app/crumbs/CrumbContext";
import DataGrid from "@poseidon/ui/components/DataGrid";
import { DataGridContainer } from "@poseidon/ui/components/DataGridContainer";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { getCaisColumns } from "./caisColumns";
import { useCaisReport } from "./useCaisReport";

/**
 * Live preview of the monthly Experian CAIS submission: one row per party
 * to every active or settled agreement, plus a button that downloads the
 * fixed-width file as at the moment it is clicked.
 */
export default function CaisReport() {
  useCrumb("/reports/cais/", { to: "/reports/cais", text: "CAIS Report" });
  const { setError } = useErrorMessage();
  const { report, loading, downloading, refresh, download } = useCaisReport();
  const columns = getCaisColumns();
  const rows = report?.rows ?? [];
  const withWarnings = rows.filter((row) => row.warningCount > 0).length;
  const excluded = rows.filter((row) => row.excludedFromFile).length;

  return (
    <Stack spacing={3} sx={{ px: 5, pb: 5, flex: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h5">CAIS Report</Typography>
          <Typography variant="body2" color="text.secondary">
            {report
              ? `${rows.length} records as at ${report.generatedAt.toLocaleString(
                  "en-GB"
                )}`
              : "Generating…"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={loading || downloading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={
              downloading ? <CircularProgress size={18} /> : <DownloadIcon />
            }
            onClick={download}
            disabled={loading || downloading}
          >
            Export CAIS file
          </Button>
        </Stack>
      </Stack>
      {excluded > 0 && (
        <Alert severity="error">
          {excluded} agreement{excluded === 1 ? "" : "s"} could not be loaded
          and will be left out of the exported file. See the warning on the
          row for the reason.
        </Alert>
      )}
      {withWarnings > 0 && (
        <Alert severity="warning">
          {withWarnings} record{withWarnings === 1 ? "" : "s"} have data
          warnings. Hover the warning count on a row to see what is missing;
          fix the sailor or agreement before submitting the file.
        </Alert>
      )}
      {loading && !report ? (
        <CircularProgress sx={{ alignSelf: "center" }} />
      ) : (
        <DataGridContainer>
          <DataGrid
            id="cais-report"
            useSessionStorageForPagination
            columns={columns}
            rows={rows}
            setError={setError}
            loading={loading}
            initialState={{
              sorting: { sortModel: [{ field: "accountNumber", sort: "asc" }] },
            }}
          />
        </DataGridContainer>
      )}
    </Stack>
  );
}
